import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo, useState } from "react";

import styles from "./Accordion.module.scss";

export type AccordionHeadingLevel = "h2" | "h3" | "h4" | "h5" | "h6";

export type AccordionItemIdentifier = {
  id: string;
  title: string;
};

export type AccordionContextValues = {
  expandedIds: string[];
  toggleAccordionItem: (item: AccordionItemIdentifier) => void;
  headingLevel: AccordionHeadingLevel;
  useRegionRole: boolean;
};

export const AccordionContext = createContext<AccordionContextValues | null>(null);

export const useAccordionContext = () => useContext(AccordionContext);

export type AccordionProps = {
  singleExpanded?: boolean;
  defaultExpanded?: string[];
  onToggle?: (item: AccordionItemIdentifier, isExpanded: boolean) => void;
  headingLevel?: AccordionHeadingLevel;
  useRegionRole?: boolean;
  children?: ReactNode;
};

export const Accordion = ({
  singleExpanded = false,
  defaultExpanded = [],
  onToggle,
  headingLevel = "h3",
  useRegionRole = true,
  children,
}: AccordionProps) => {
  const [expandedItems, setExpandedItems] = useState<string[]>(defaultExpanded);

  const toggleAccordionItem = useCallback(
    (item: AccordionItemIdentifier) => {
      setExpandedItems((prev) => {
        const isCurrentlyExpanded = prev.includes(item.id);

        if (isCurrentlyExpanded) {
          onToggle?.(item, false);
          return prev.filter((expandedId) => expandedId !== item.id);
        }

        onToggle?.(item, true);
        return singleExpanded ? [item.id] : [...prev, item.id];
      });
    },
    [singleExpanded, onToggle]
  );

  const value = useMemo(
    () => ({ expandedIds: expandedItems, toggleAccordionItem, headingLevel, useRegionRole }),
    [expandedItems, toggleAccordionItem, headingLevel, useRegionRole]
  );

  return (
    <AccordionContext.Provider value={value}>
      <div className={styles.accordionContainer}>{children}</div>
    </AccordionContext.Provider>
  );
};
